import { BaseComponent } from './BaseComponent';
import { Announcement } from '../types';
import { Config } from '../core/Config';
import { getTypeIcon } from '../utils/icons';

export class PersistentTopBar extends BaseComponent {
    // Exposed on <html> while the bar is visible, holding the bar's current
    // height. Only relevant to host pages whose own header/sidebar is
    // position:fixed or sticky (which a body margin can't push down): they can
    // offset that chrome with one rule, e.g.
    // `header { top: var(--pr-persistent-topbar-offset, 0px) }`.
    private static readonly OFFSET_VAR = '--pr-persistent-topbar-offset';

    private announcement: Announcement;
    private onReadMore: () => void;
    private originalBodyMarginTop: string = '';
    private originalBodyTransition: string = '';
    private bodyStylesApplied: boolean = false;
    private onResize: (() => void) | null = null;

    constructor(config: Config, announcement: Announcement, onReadMore: () => void) {
        super(config);
        this.announcement = announcement;
        this.onReadMore = onReadMore;
    }

    protected render(): HTMLElement {
        const topBar = this.createElement('div', 'pr-persistent-topbar pr-sdk');
        topBar.setAttribute('role', 'banner');
        topBar.setAttribute('aria-label', 'Persistent Announcement');

        const content = this.createElement('div', 'pr-persistent-topbar-content');

        // Add type icon (outline SVG that inherits the bar's text color)
        const typeIcon = this.createElement('span', 'pr-persistent-topbar-icon');
        typeIcon.innerHTML = getTypeIcon(this.announcement.type);
        typeIcon.setAttribute('aria-hidden', 'true');
        content.appendChild(typeIcon);

        const title = this.createElement('h3', 'pr-persistent-topbar-title');
        title.textContent = this.announcement.title;
        content.appendChild(title);

        const link = this.createElement('a', 'pr-persistent-topbar-link');
        link.textContent = this.config.t.readMore;
        link.href = '#';
        link.onclick = (e) => {
            e.preventDefault();
            this.handleReadMore();
        };
        content.appendChild(link);

        topBar.appendChild(content);

        return topBar;
    }

    public show(): void {
        // Mount the bar first; only push the host page's body down once the
        // bar is actually in the DOM. Calling addBodyMargin from render()
        // applied styles even when super.show() ultimately failed, which
        // could leave the customer's page with a 60px gap and our bar
        // missing.
        super.show();
        this.addBodyMargin();
    }

    private addBodyMargin(): void {
        if (this.bodyStylesApplied) {
            return;
        }

        const body = document.body;
        // Measure the bar's real rendered height so the offset matches even when
        // the title wraps to two lines (common on narrow/mobile viewports). The
        // old fixed 60px left a gap or an overlap whenever the height differed.
        const barHeight = this.container?.offsetHeight || 60;

        // Snapshot whatever the customer had set so teardown returns the page
        // exactly how it was. The previous version assumed both were empty,
        // which clobbered host-app styling.
        this.originalBodyMarginTop = body.style.marginTop;
        this.originalBodyTransition = body.style.transition;
        this.bodyStylesApplied = true;

        body.style.marginTop = `${barHeight}px`;
        body.style.transition = 'margin-top 0.3s ease';
        document.documentElement.style.setProperty(PersistentTopBar.OFFSET_VAR, `${barHeight}px`);

        // The bar's height changes when the title wraps/unwraps at a different
        // viewport width, so recompute the body margin and offset var on resize —
        // otherwise the page keeps the initial (now wrong) gap.
        this.onResize = () => this.syncOffset();
        window.addEventListener('resize', this.onResize);
    }

    /**
     * Re-measure the bar and update the body margin + offset var to match its
     * current rendered height.
     */
    private syncOffset(): void {
        if (!this.bodyStylesApplied || !this.container) {
            return;
        }

        const barHeight = this.container.offsetHeight || 60;
        document.body.style.marginTop = `${barHeight}px`;
        document.documentElement.style.setProperty(PersistentTopBar.OFFSET_VAR, `${barHeight}px`);
    }

    private removeBodyMargin(): void {
        if (!this.bodyStylesApplied) {
            return;
        }

        if (this.onResize) {
            window.removeEventListener('resize', this.onResize);
            this.onResize = null;
        }

        const body = document.body;
        // When the original was empty we drop the property entirely instead
        // of leaving an inline empty value sitting on the customer's body.
        if (this.originalBodyMarginTop) {
            body.style.marginTop = this.originalBodyMarginTop;
        } else {
            body.style.removeProperty('margin-top');
        }

        if (this.originalBodyTransition) {
            body.style.transition = this.originalBodyTransition;
        } else {
            body.style.removeProperty('transition');
        }

        document.documentElement.style.removeProperty(PersistentTopBar.OFFSET_VAR);

        this.bodyStylesApplied = false;
    }

    private handleReadMore(): void {
        this.onReadMore();
    }

    public hide(): void {
        this.removeBodyMargin();
        super.hide();
    }
}
